import {
  Container,
  Typography,
  IconButton,
  Tooltip,
  TableBody,
  TableCell,
  TableRow,
  Table,
  TableContainer,
  TableHead,
  Paper,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import NavigationHeader from "../../../components/NavigationHeader";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { Client } from "./types";
import ClientActionModal from "./ClientActionModal";
import { clientApi } from "./apiService";
import { useFirebaseAuth } from "../../../hooks/useFirebaseAuth";
import { Loading } from "../../../components/Loading";
import { tableHeaderStyles, tableCellStyles } from "../../../utils/styles";

const Clients = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, loading: authLoading } = useFirebaseAuth();
  const [shouldFetchClients, setShouldFetchClients] = useState(true);

  useEffect(() => {
    if (shouldFetchClients) {
      setIsLoading(true);
      clientApi
        .getClients()
        .then((clients) => {
          setClients(clients);
        })
        .finally(() => {
          setIsLoading(false);
          setShouldFetchClients(false);
        });
    }
  }, [shouldFetchClients]);

  const handleAddCompany = () => {
    setOpen(true);
    setSelectedClient(null);
  };

  const handleEditClient = (updatedClient: Client) => {
    setOpen(true);
    setSelectedClient(updatedClient);
  };

  if (authLoading || isLoading || isSubmitting) {
    return isSubmitting ? (
      <Loading message="Submitting Request..." fullScreen={true} />
    ) : (
      <Loading message="Loading Clients..." fullScreen={true} />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Master Data", path: "/master-data" },
          { label: "Clients" },
        ]}
        title="Clients"
        button={{
          label: "Add Client",
          onClick: handleAddCompany,
          icon: <AddIcon />,
        }}
      />

      {clients?.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
          No clients found
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="companies table">
            <TableHead>
              <TableRow>
                <TableCell {...tableHeaderStyles}>Client Name</TableCell>
                <TableCell {...tableHeaderStyles}>Contact Name</TableCell>
                <TableCell {...tableHeaderStyles}>Contact Number</TableCell>
                <TableCell {...tableHeaderStyles}>Email</TableCell>
                <TableCell {...tableHeaderStyles} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients?.map((client) => (
                <TableRow
                  key={client?.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <TableCell {...tableCellStyles}>
                    {client?.clientName}
                  </TableCell>
                  <TableCell {...tableCellStyles}>
                    {client?.primaryContactName || "-"}
                  </TableCell>
                  <TableCell {...tableCellStyles}>
                    {client?.primaryContactNumber || "-"}
                  </TableCell>
                  <TableCell {...tableCellStyles}>
                    {client?.email || "-"}
                  </TableCell>
                  <TableCell {...tableCellStyles} align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditClient(client)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ClientActionModal
        open={open}
        onClose={() => setOpen(false)}
        client={selectedClient}
        isEdit={selectedClient !== null}
        user={user}
        setIsSubmitting={setIsSubmitting}
        setClients={setClients}
        setIsLoading={setIsLoading}
        setShouldFetchClients={setShouldFetchClients}
      />
    </Container>
  );
};

export default Clients;
